import React from "react";
import LegalLayout from "./LegalLayout";

export default function CookiePolicy() {
  return (
    <LegalLayout
      title="Cookie Policy"
      sub="Cookie & tracker"
      render={(lang) => (lang === "it" ? <ItContent /> : <EnContent />)}
    />
  );
}

function ItContent() {
  return (
    <>
      <p>
        Questa Cookie Policy spiega quali cookie e tecnologie simili (es. <code>localStorage</code>) sono utilizzati da Reelio (reelio.it) e come puoi gestirne le preferenze.
      </p>

      <h2>1. Cosa sono i cookie</h2>
      <p>
        I cookie sono piccoli file di testo che i siti web salvano nel browser dell'utente. Reelio utilizza anche tecnologie simili come <code>localStorage</code> per memorizzare il token di sessione (JWT) necessario al login.
      </p>

      <h2>2. Categorie di cookie utilizzate</h2>

      <h3>2.1 Cookie tecnici essenziali (sempre attivi)</h3>
      <p>Necessari per il funzionamento del Servizio. Non richiedono consenso ai sensi del provvedimento del Garante del 10/06/2021.</p>
      <table>
        <thead>
          <tr><th>Nome</th><th>Tipo</th><th>Finalità</th><th>Durata</th></tr>
        </thead>
        <tbody>
          <tr><td><code>sr_token</code></td><td>localStorage</td><td>Token JWT di sessione utente</td><td>Fino al logout</td></tr>
          <tr><td><code>sr_admin_token</code></td><td>localStorage</td><td>Token impersonation admin (solo admin)</td><td>Fino al logout</td></tr>
          <tr><td><code>reelio_consent</code></td><td>localStorage</td><td>Memorizza la scelta sui cookie</td><td>12 mesi</td></tr>
          <tr><td>Cookie Stripe</td><td>Cookie terza parte</td><td>Sicurezza pagamenti durante checkout</td><td>Sessione</td></tr>
          <tr><td>Cookie Cloudflare</td><td>Cookie terza parte</td><td>Protezione DDoS, performance CDN</td><td>30 minuti / 1 anno</td></tr>
        </tbody>
      </table>

      <h3>2.2 Cookie analitici (richiedono consenso)</h3>
      <table>
        <thead>
          <tr><th>Nome</th><th>Fornitore</th><th>Finalità</th><th>Durata</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><code>ph_*</code></td>
            <td>PostHog Inc. (USA, SCC)</td>
            <td>Statistiche aggregate di utilizzo del prodotto, analisi UX, eventi di engagement</td>
            <td>Fino a 12 mesi</td>
          </tr>
        </tbody>
      </table>

      <h3>2.3 Cookie di marketing (richiedono consenso)</h3>
      <p>Al momento <strong>non utilizziamo cookie di marketing</strong>. Lo spazio nel banner cookie è riservato per future campagne pubblicitarie (Google Ads, Meta Pixel) — se attivati, sarai informato e potrai esprimere un nuovo consenso.</p>

      <h2>3. Come gestire i cookie</h2>
      <p>Al primo accesso a Reelio compare un banner che ti consente di:</p>
      <ul>
        <li><strong>Accetta tutto</strong> — attiva cookie tecnici, analitici e marketing.</li>
        <li><strong>Solo essenziali</strong> — attiva solo i cookie tecnici (default per chi non sceglie).</li>
        <li><strong>Personalizza</strong> — selezione granulare per categoria.</li>
      </ul>
      <p>
        Puoi <strong>modificare le preferenze in qualsiasi momento</strong> cliccando "Impostazioni cookie" in fondo a ogni pagina. Puoi anche cancellare cookie e <code>localStorage</code> dal tuo browser (vedi guide per <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a>, <a href="https://support.mozilla.org/it/kb/elimina-cookie" target="_blank" rel="noopener noreferrer">Firefox</a>, <a href="https://support.apple.com/it-it/guide/safari/sfri11471" target="_blank" rel="noopener noreferrer">Safari</a>).
      </p>

      <h2>4. Cookie di terze parti</h2>
      <p>Alcuni cookie sono installati direttamente da fornitori terzi (Stripe, Cloudflare, PostHog). Le rispettive informative sono disponibili sui loro siti:</p>
      <ul>
        <li><a href="https://stripe.com/it/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a></li>
        <li><a href="https://www.cloudflare.com/it-it/privacypolicy/" target="_blank" rel="noopener noreferrer">Cloudflare Privacy Policy</a></li>
        <li><a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">PostHog Privacy Policy</a></li>
      </ul>

      <h2>5. Modifiche</h2>
      <p>Aggiornamenti a questa Cookie Policy saranno pubblicati su questa pagina.</p>
    </>
  );
}

function EnContent() {
  return (
    <>
      <p>This Cookie Policy explains which cookies and similar technologies (e.g. <code>localStorage</code>) Reelio uses and how you can manage your preferences.</p>

      <h2>1. What are cookies</h2>
      <p>Small text files saved by websites in your browser. Reelio also uses <code>localStorage</code> to store the JWT session token required for login.</p>

      <h2>2. Categories used</h2>

      <h3>2.1 Strictly necessary cookies (always active)</h3>
      <table>
        <thead><tr><th>Name</th><th>Type</th><th>Purpose</th><th>Duration</th></tr></thead>
        <tbody>
          <tr><td><code>sr_token</code></td><td>localStorage</td><td>User session JWT</td><td>Until logout</td></tr>
          <tr><td><code>sr_admin_token</code></td><td>localStorage</td><td>Admin impersonation token</td><td>Until logout</td></tr>
          <tr><td><code>reelio_consent</code></td><td>localStorage</td><td>Stores cookie consent</td><td>12 months</td></tr>
          <tr><td>Stripe cookies</td><td>Third-party</td><td>Checkout security</td><td>Session</td></tr>
          <tr><td>Cloudflare cookies</td><td>Third-party</td><td>DDoS protection, CDN</td><td>30 min / 1 year</td></tr>
        </tbody>
      </table>

      <h3>2.2 Analytics cookies (consent required)</h3>
      <table>
        <thead><tr><th>Name</th><th>Vendor</th><th>Purpose</th><th>Duration</th></tr></thead>
        <tbody>
          <tr><td><code>ph_*</code></td><td>PostHog Inc. (USA, SCC)</td><td>Aggregated product usage analytics</td><td>Up to 12 months</td></tr>
        </tbody>
      </table>

      <h3>2.3 Marketing cookies (consent required)</h3>
      <p>Currently <strong>not in use</strong>. Reserved for future advertising campaigns; if activated you will be asked for a fresh consent.</p>

      <h2>3. Managing cookies</h2>
      <p>On first visit a banner lets you choose: Accept all / Essentials only / Customize. You can change your preferences anytime via "Cookie settings" in the footer.</p>

      <h2>4. Third-party cookies</h2>
      <ul>
        <li><a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a></li>
        <li><a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">Cloudflare Privacy Policy</a></li>
        <li><a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">PostHog Privacy Policy</a></li>
      </ul>

      <h2>5. Changes</h2>
      <p>Updates will be published on this page.</p>
    </>
  );
}
